import { DateAdapterBase, InvalidDateAdapterError, ArgumentError } from '@rschedule/core';

class StandardDateAdapter extends DateAdapterBase {
  constructor(date, options = {}) {
    super(undefined, options);
    if (!['UTC', null, undefined].includes(options.timezone)) {
      throw new InvalidDateAdapterError(
        `StandardDateAdapter only supports "UTC" and ` +
          `local time zones but "${options.timezone}" was provided.`,
      );
    }
    this._date = new Date(date);
    this.timezone = options.timezone !== undefined ? options.timezone : null;
    if (options.metadata) {
      Object.assign(this.metadata, options.metadata);
    }
    this.assertIsValid();
  }
  static isDate(object) {
    return Object.prototype.toString.call(object) === '[object Date]';
  }
  static fromDate(date, options) {
    return new StandardDateAdapter(date, options);
  }
  static fromJSON(json) {
    const args = [
      json.year,
      json.month - 1,
      json.day,
      json.hour,
      json.minute,
      json.second,
      json.millisecond,
    ];
    let date;
    switch (json.timezone) {
      case 'UTC': {
        date = new StandardDateAdapter(new Date(Date.UTC(...args)), {
          timezone: 'UTC',
          duration: json.duration,
        });
        break;
      }
      case null: {
        date = new StandardDateAdapter(new Date(...args), {
          timezone: null,
          duration: json.duration,
        });
        break;
      }
      default:
        throw new InvalidDateAdapterError(
          'The `StandardDateAdapter` only supports datetimes in ' +
            `UTC or LOCAL (null) time. You provided a JSON object ` +
            `with timezone "${json.timezone}".`,
        );
    }
    if (json.metadata) {
      Object.assign(date.metadata, json.metadata);
    }
    return date;
  }
  static fromDateTime(datetime) {
    const date = StandardDateAdapter.fromJSON(datetime.toJSON());
    date.generators.push(...datetime.generators);
    if (datetime.metadata) {
      Object.assign(date.metadata, datetime.metadata);
    }
    return date;
  }
  get date() {
    return new Date(this._date);
  }
  get end() {
    if (!this.duration) return;
    if (this._end) return this._end;
    this._end = StandardDateAdapter.fromDateTime(
      this.toDateTime().add(this.duration, 'millisecond'),
    ).date;
    return this._end;
  }
  set(prop, value) {
    if (prop === 'timezone') {
      if (this.timezone === value) return this;
      else {
        return new StandardDateAdapter(this._date, {
          timezone: value,
          duration: this.duration,
          generators: this.generators,
        });
      }
    } else if (prop === 'duration') {
      if (this.duration === value) return this;
      else {
        return new StandardDateAdapter(this._date, {
          timezone: this.timezone,
          duration: value,
          generators: this.generators,
        });
      }
    }
    throw new ArgumentError(`Unknown prop "${prop}" for StandardDateAdapter#set()`);
  }
  valueOf() {
    return this._date.valueOf();
  }
  toJSON() {
    let json;
    if (this.timezone === 'UTC') {
      json = {
        timezone: this.timezone,
        year: this._date.getUTCFullYear(),
        month: this._date.getUTCMonth() + 1,
        day: this._date.getUTCDate(),
        hour: this._date.getUTCHours(),
        minute: this._date.getUTCMinutes(),
        second: this._date.getUTCSeconds(),
        millisecond: this._date.getUTCMilliseconds(),
      };
    } else {
      json = {
        timezone: this.timezone,
        year: this._date.getFullYear(),
        month: this._date.getMonth() + 1,
        day: this._date.getDate(),
        hour: this._date.getHours(),
        minute: this._date.getMinutes(),
        second: this._date.getSeconds(),
        millisecond: this._date.getMilliseconds(),
      };
    }
    if (this.duration) {
      json.duration = this.duration;
    }
    return json;
  }
  assertIsValid() {
    if (!StandardDateAdapter.isDate(this._date) || isNaN(this._date.valueOf())) {
      throw new InvalidDateAdapterError('StandardDateAdapter has invalid date.');
    } else if (![null, 'UTC'].includes(this.timezone)) {
      throw new InvalidDateAdapterError(
        'StandardDateAdapter only supports local (null) and UTC timezones but ' +
          `"${this.timezone}" was specified.`,
      );
    } else if (this.duration && this.duration <= 0) {
      throw new InvalidDateAdapterError('If provided, duration must be greater than 0.');
    }
    return true;
  }
}
StandardDateAdapter.hasTimezoneSupport = false;

export { StandardDateAdapter };
